import { PricingBreakdown } from '@/types/pricing';
import { SurfaceAxes } from '@/types/surfaces';
import { SurfaceImageMap, SurfaceTextMap } from '@/types/personalization';
import { ProjectUpload } from '@/types/uploads';
import { BoxModelStyle } from '@/three/types';

export enum CreatorStep {
	CONFIGURATION,
	PERSONALIZATION,
}

export enum CreatorStage {
	ENTRY = 'ENTRY',
	DEFAULT_VARIANTS = 'DEFAULT_VARIANTS',
	CREATOR = 'CREATOR',
}

export enum ViewType {
	'2D' = '2D',
	'3D' = '3D',
}

export enum LayerType {
	INTERNAL = 'INTERNAL',
	EXTERNAL = 'EXTERNAL',
}

export type SelectedSurfaceElement =
	| {
			type: 'text' | 'image';
			surfaceId: string;
			id: string;
	  }
	| null;

export type SurfaceViewMode = 'NET' | 'SURFACE';

export interface CreatorOptions {
	isProductTypeInfoOpened: boolean;
	isFullPreviewOpened: boolean;
	currentStep: CreatorStep;
	viewType: ViewType;
	layerType: LayerType;
	isSideView: boolean;
	selectedDecor: string | null;
	activeSurfaceId: string | null;
	activeSurfaceAxes: SurfaceAxes | null;
	hoverSurfaceId: string | null;
	surfaceViewMode: SurfaceViewMode;
	selectedElement: SelectedSurfaceElement;
	isBoxOpened: boolean;
	personalizationMode: 'designer' | 'upload';
}

export interface CreatorRendererOptions {}

export interface CreatorAutosaveState {
	lastSavedAt: number | null;
	lastRestoredAt: number | null;
	restoredFromSnapshot: boolean;
	lastChangeAt: number | null;
	lastSnackbarAt: number | null;
}

export interface CreatorSnapshot {
	selectedProductTypeId: string | null;
	selectedProductBoxStyle?: BoxModelStyle | null;
	selectedVariantId: string | null;
	configuration?: Record<string, string | number | null>;
	options?: Partial<CreatorOptions>;
	pricing?: PricingBreakdown | null;
	textElements?: SurfaceTextMap;
	imageElements?: SurfaceImageMap;
	savedAt?: number | null;
	uploads?: ProjectUpload[];
	apiProjectId?: string | null;
	isTemplateEditor?: boolean;
	templateRequestId?: string | null;
	isViewOnly?: boolean;
}

export interface CreatorRuntimeSettings {
	autosaveInterval: number;
	uploadMaxSize: number;
	fallbackTemplateIds: string[];
}

export interface CreatorState {
	options: CreatorOptions;
	stage: CreatorStage;
	selectedProductTypeId: string | null;
	selectedProductBoxStyle: BoxModelStyle;
	selectedVariantId: string | null;
	pendingPresetAttributes: Record<string, string | number | (string | number)[]> | null;
	configuration: Record<string, string | number | null>;
	pricing: CreatorPricingState;
	textElements: SurfaceTextMap;
	imageElements: SurfaceImageMap;
	uploads: ProjectUpload[];
	autosave: CreatorAutosaveState;
	settings: CreatorRuntimeSettings;
	apiProjectId: string | null;
	submission: {
		status: 'idle' | 'pending' | 'success' | 'error';
		error: string | null;
	};
	isTemplateEditor: boolean;
	templateRequestId: string | null;
	isViewOnly: boolean;
	/** Quote-mail summary from the hydrated project; used to restore stripped tape fields. */
	quoteSummaryRows: { label: string; value: string }[] | null;
}

export interface CreatorPricingState {
	status: 'idle' | 'loading' | 'succeeded' | 'failed';
	breakdown: PricingBreakdown | null;
	error: string | null;
	requiresQuote: boolean;
}
